import { Component } from '@theme/component';

const SCROLL_STORAGE_KEY = 'email-signup-scroll-top';
const FORM_SELECTOR = 'form.email-signup__form';

/**
 * Mirrors the theme scroll container rules from scroll-container.js without
 * importing that module (keeps newsletter submit working even if it fails).
 * @returns {Element}
 */
function getScrollContainer() {
  if (window.matchMedia('(min-width: 990px)').matches) {
    return document.querySelector('.page-wrapper') ?? document.scrollingElement ?? document.documentElement;
  }
  return document.scrollingElement ?? document.documentElement;
}

/**
 * Persist scroll and strip the form action hash so Shopify's hCaptcha-protected
 * native POST can proceed without a post-redirect jump to #contact_form.
 * @param {HTMLFormElement} form
 */
function prepareEmailSignupSubmit(form) {
  persistScrollPosition();
  disableSmoothScroll();

  try {
    const actionUrl = new URL(form.action || `${window.location.origin}/contact`, window.location.href);
    actionUrl.hash = '';
    form.action = `${actionUrl.pathname}${actionUrl.search}`;
  } catch {
    // Ignore URL parse failures; native submit still proceeds.
  }
}

/**
 * @typedef {Object} EmailSignupRefs
 * @property {HTMLElement} message - Status message container
 * @property {HTMLTemplateElement} [successTemplate] - Success message template
 * @property {HTMLTemplateElement} [errorTemplate] - Error message template
 * @property {HTMLButtonElement} [submitButton] - Submit button
 */

/**
 * Newsletter signup uses Shopify's native customer form POST so hCaptcha can
 * run. We only prepare scroll restoration — fetch/AJAX bypasses captcha and
 * returns 400 from /contact.
 *
 * @extends {Component<EmailSignupRefs>}
 */
class EmailSignupComponent extends Component {
  requiredRefs = ['message'];

  connectedCallback() {
    super.connectedCallback();

    const form = this.#getForm();
    if (!form) return;

    form.addEventListener('submit', this.#onSubmit);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#getForm()?.removeEventListener('submit', this.#onSubmit);
  }

  /**
   * @param {SubmitEvent} event
   */
  #onSubmit = (event) => {
    const form = event.currentTarget;
    if (!(form instanceof HTMLFormElement)) return;

    // Do not preventDefault — Shopify's storefront hCaptcha listens for submit
    // and will reject token-less fetch/AJAX posts with HTTP 400.
    prepareEmailSignupSubmit(form);
  };

  /**
   * @returns {HTMLFormElement | null}
   */
  #getForm() {
    return /** @type {HTMLFormElement | null} */ (this.querySelector(FORM_SELECTOR));
  }
}

function persistScrollPosition() {
  try {
    sessionStorage.setItem(SCROLL_STORAGE_KEY, String(getScrollContainer().scrollTop));
  } catch {
    // Ignore storage failures (private mode quotas, etc.).
  }
}

function disableSmoothScroll() {
  document.documentElement.classList.add('email-signup--no-smooth-scroll');

  if (document.getElementById('email-signup-no-smooth-scroll')) return;

  const style = document.createElement('style');
  style.id = 'email-signup-no-smooth-scroll';
  style.textContent =
    'html.email-signup--no-smooth-scroll, html.email-signup--no-smooth-scroll .page-wrapper { scroll-behavior: auto !important; }';
  document.documentElement.appendChild(style);
}

/**
 * If Shopify returned via full-page POST redirect, restore scroll instantly and
 * clean customer_posted / hash from the URL.
 */
function recoverFromFullPageSubmit() {
  const url = new URL(window.location.href);
  const posted = url.searchParams.get('customer_posted') === 'true';
  if (!posted && !document.documentElement.classList.contains('email-signup--no-smooth-scroll')) {
    return;
  }

  disableSmoothScroll();

  let savedScroll = null;
  let savedHash = '';
  try {
    const raw = sessionStorage.getItem(SCROLL_STORAGE_KEY);
    if (raw != null) savedScroll = Number(raw);
    sessionStorage.removeItem(SCROLL_STORAGE_KEY);
    savedHash = sessionStorage.getItem('email-signup-hash') || url.hash || '';
    sessionStorage.removeItem('email-signup-hash');
  } catch {
    // Ignore storage failures.
  }

  const restore = () => {
    const container = getScrollContainer();

    if (Number.isFinite(savedScroll) && /** @type {number} */ (savedScroll) >= 0) {
      container.scrollTo({ top: /** @type {number} */ (savedScroll), behavior: 'instant' });
    } else {
      const hashId = savedHash.replace(/^#/, '');
      const target =
        (hashId && document.getElementById(hashId)) ||
        document.querySelector('.email-signup__message[data-status="success"]') ||
        document.querySelector('email-signup-component');

      if (target instanceof HTMLElement) {
        const top = target.getBoundingClientRect().top + container.scrollTop;
        container.scrollTo({ top: Math.max(0, top - 40), behavior: 'instant' });
      }
    }

    url.searchParams.delete('customer_posted');
    url.hash = '';
    history.replaceState(history.state, '', url.pathname + url.search);
    document.documentElement.classList.remove('email-signup--no-smooth-scroll');
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', restore, { once: true });
  } else {
    requestAnimationFrame(restore);
  }
}

recoverFromFullPageSubmit();

if (!customElements.get('email-signup-component')) {
  customElements.define('email-signup-component', EmailSignupComponent);
}
